/**
 * Paraşüt API V4 Entegrasyon Yardımcısı
 */

const PARASUT_CONFIG = {
  clientId: process.env.REACT_APP_PARASUT_CLIENT_ID,
  clientSecret: process.env.REACT_APP_PARASUT_CLIENT_SECRET,
  username: process.env.REACT_APP_PARASUT_USERNAME,
  password: process.env.REACT_APP_PARASUT_PASSWORD,
  companyId: process.env.REACT_APP_PARASUT_COMPANY_ID,
};

const PARASUT_API_BASE = "/api/parasut/v4";
const TOKEN_URL = "/api/parasut/oauth/token";

// Paraşüt web arayüzü deep-link
export const getParasutWebLink = (parasutId, direction = "SALES") => {
  if (!parasutId) return null;
  const company = PARASUT_CONFIG.companyId;
  if (!company) return null;
  // Paraşüt UI path'leri — purchase_bills → fis-faturalar, sales_invoices → satislar
  const path = direction === "PURCHASE"
    ? `fis-faturalar/${parasutId}`
    : `satislar/${parasutId}`;
  return `https://uygulama.parasut.com/${company}/${path}`;
};

let _cachedToken = null;
let _tokenExpiresAt = 0;

export const getParasutToken = async () => {
  const now = Date.now() / 1000;
  if (_cachedToken && now < _tokenExpiresAt - 60) {
    return _cachedToken;
  }

  const response = await fetch(TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      grant_type: "password",
      client_id: PARASUT_CONFIG.clientId,
      client_secret: PARASUT_CONFIG.clientSecret,
      username: PARASUT_CONFIG.username,
      password: PARASUT_CONFIG.password,
      redirect_uri: "urn:ietf:wg:oauth:2.0:oob",
    }),
  });

  if (!response.ok) throw new Error("Paraşüt token alınamadı.");

  const data = await response.json();
  _cachedToken = data.access_token;
  _tokenExpiresAt = now + (data.expires_in || 7200);
  return _cachedToken;
};

const parasutFetch = async (path, options = {}) => {
  const token = await getParasutToken();
  const url = `${PARASUT_API_BASE}/${PARASUT_CONFIG.companyId}${path}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.errors?.[0]?.detail || `HTTP ${response.status}`);
  }
  return data;
};

// Ürün/hizmet kartı bul veya oluştur
export const findOrCreateProduct = async (name, vatRate) => {
  const productName = name ? name.trim() : "Hizmet Bedeli";
  
  const search = await parasutFetch(`/products?filter[name]=${encodeURIComponent(productName)}`);
  if (search?.data?.length > 0) {
    return search.data[0].id;
  }

  const payload = {
    data: {
      type: "products",
      attributes: {
        name: productName,
        vat_rate: vatRate !== null && vatRate !== undefined ? parseFloat(vatRate) : 20,
        unit: "Adet",
        list_price: 0,
        currency: "TRL",
        inventory_tracking: false
      }
    }
  };

  const created = await parasutFetch("/products", {
    method: "POST",
    body: JSON.stringify(payload)
  });

  return created.data.id;
};

export const findOrCreateContact = async ({ name, taxNumber, taxOffice, email, address, phone, iban, city, district, accountType = "customer" }) => {
  if (taxNumber) {
    const search = await parasutFetch(`/contacts?filter[tax_number]=${encodeURIComponent(taxNumber)}`);
    if (search?.data?.length > 0) return search.data[0].id;
  }

  const isCompany = taxNumber && taxNumber.length === 10;
  const attributes = {
    name: name || "İsimsiz Kişi",
    contact_type: isCompany ? "company" : "person",
    account_type: accountType, // 'customer' | 'supplier'
  };
  if (taxNumber) attributes.tax_number = taxNumber;
  if (taxOffice) attributes.tax_office = taxOffice;
  if (email) attributes.email = email;
  if (address) attributes.address = address;
  if (phone) attributes.phone = phone;
  if (iban) attributes.iban = iban;
  if (city) attributes.city = city;
  if (district) attributes.district = district;

  const created = await parasutFetch("/contacts", {
    method: "POST",
    body: JSON.stringify({ data: { type: "contacts", attributes } }),
  });
  return created.data.id;
};

export const createSalesInvoice = async (invoice, contactId) => {
  const issueDate = invoice.Created_DateTime
    ? new Date(invoice.Created_DateTime).toISOString().split("T")[0]
    : new Date().toISOString().split("T")[0];

  const invoiceNumeric = parseInt(String(invoice.Invoince_Number || "").replace(/\D/g, ""), 10) || invoice.ID;

  let invoiceLinesData = [];
  const rawLines = invoice.Invoince_Lines || [];

  if (rawLines.length > 0) {
    for (const line of rawLines) {
      const pName = (line.ProductName || "Araç Taşıma Hizmet Bedeli").trim();
      
      // KDV Hatası Çözümü (%0 kabul edilir)
      const pVat = line.VatRate !== null && line.VatRate !== undefined ? parseFloat(line.VatRate) : 20;
      
      const productId = await findOrCreateProduct(pName, pVat);

      invoiceLinesData.push({
        type: "sales_invoice_details",
        attributes: {
          quantity: Math.max(parseFloat(line.Quantity) || 1, 1),
          unit_price: Math.max(parseFloat(line.UnitPrice) || 0, 0.01),
          vat_rate: pVat,
          description: pName
        },
        relationships: {
          product: {
            data: { type: "products", id: String(productId) }
          }
        }
      });
    }
  } else {
    // Fallback: Kalem yoksa
    const amountHT = parseFloat(invoice.Amount_HT) || 1.0;
    const pName = "Araç Taşıma Operasyon Hizmeti";
    const productId = await findOrCreateProduct(pName, 20);

    invoiceLinesData.push({
      type: "sales_invoice_details",
      attributes: {
        quantity: 1,
        unit_price: amountHT,
        vat_rate: 20,
        description: pName
      },
      relationships: {
        product: {
          data: { type: "products", id: String(productId) }
        }
      }
    });
  }

  // is_detailed: true → Paraşüt UI'da "Satış Faturaları" listesine düşer
  // is_detailed: false → "Satış Fişleri / Hızlı Satışlar" tarafına düşer
  const payload = {
    data: {
      type: "sales_invoices",
      attributes: {
        item_type: "invoice",
        description: `LojGo - Fatura - ${invoice.Invoince_Number || invoice.ID}`,
        issue_date: issueDate,
        due_date: issueDate,
        invoice_series: "LJG",
        invoice_id: String(invoiceNumeric),
        currency: "TRL",
        is_detailed: true,
        shipment_included: false,
      },
      relationships: {
        contact: {
          data: { type: "contacts", id: String(contactId) },
        },
        details: {
          data: invoiceLinesData
        }
      },
    },
  };

  const result = await parasutFetch("/sales_invoices", {
    method: "POST",
    body: JSON.stringify(payload),
  });

  return { parasutInvoiceId: result.data.id };
};

export const syncInvoiceToParasut = async (invoice) => {
  const customer = invoice.Users || {};
  const invoiceInfo = invoice.Users_Invoince || {};
  const company = invoiceInfo.Users_Company || {}; 

  let contactId = invoiceInfo.Parasut_Contact_ID || null;

  const customerName =
    company.Users_Company_Name || 
    customer.Users_NameSurname ||
    customer.NameSurname ||
    invoiceInfo.KdvNumber || 
    "İsimsiz Müşteri";

  if (!contactId) {
    contactId = await findOrCreateContact({
      name: customerName,
      taxNumber: invoiceInfo.KdvNumber || customer.Users_Tc || customer.Tc,
      taxOffice: invoiceInfo.VergiDairesi,
      email: customer.Users_MailAddress || customer.Email || customer.email,
      address: invoiceInfo.Adres,
    });

    if (invoiceInfo.ID) {
      await fetch(`/api/hiflow/Users_Invoince(${invoiceInfo.ID})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Parasut_Contact_ID: contactId }),
      });
    }
  }

  const { parasutInvoiceId } = await createSalesInvoice(invoice, contactId);

  await fetch(`/api/hiflow/Invoince(${invoice.ID})`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ Parasut_Invoice_ID: parasutInvoiceId }),
  });

  return parasutInvoiceId;
};

// ─── ALIŞ / GİDER FATURASI (Sürücü → Sistem) ─────────────────
// Sürücü/çekiciye yapılan hizmet bedeli için Paraşüt'te purchase_bill oluşturur.
// `lines` verilirse her satırı ayrı detay olarak ekler; verilmezse tek satırlık.
export const createPurchaseBill = async ({ supplierContactId, amount, description, issueDate, vatRate = 0, lines, invoiceNo }) => {
  const date = issueDate
    ? new Date(issueDate).toISOString().split("T")[0]
    : new Date().toISOString().split("T")[0];

  const safeAmount = Math.max(parseFloat(amount) || 0, 0.01);
  const defaultVat = parseFloat(vatRate) || 0;
  const defaultName = description || "Sürücü Hizmet Bedeli";

  // Paraşüt purchase_bills için `details` zorunlu — yoksa fatura listede gözükmüyor.
  let billLines = [];
  if (Array.isArray(lines) && lines.length > 0) {
    for (const l of lines) {
      const pName = (l.description || defaultName).trim();
      const pVat = parseFloat(l.vatRate ?? vatRate) || 0;
      const productId = await findOrCreateProduct(pName, pVat);
      billLines.push({
        type: "purchase_bill_details",
        attributes: {
          quantity: 1,
          unit_price: Math.max(parseFloat(l.amount) || 0, 0.01),
          vat_rate: pVat,
          description: pName,
        },
        relationships: {
          product: { data: { type: "products", id: String(productId) } },
        },
      });
    }
  } else {
    const productId = await findOrCreateProduct(defaultName, defaultVat);
    billLines.push({
      type: "purchase_bill_details",
      attributes: {
        quantity: 1,
        unit_price: safeAmount,
        vat_rate: defaultVat,
        description: defaultName,
      },
      relationships: {
        product: { data: { type: "products", id: String(productId) } },
      },
    });
  }

  // Paraşüt'te purchase_bills için:
  //  - item_type: "purchase_bill" + is_detailed: true → "Alış Faturaları" listesine düşer
  //  - is_detailed: false → "Giderler" listesine düşer
  // Tedarikçi vergi mükellefi değilse (bireysel sürücü) → Paraşüt yine de Giderler'e
  // düşürebilir; bunu istemiyorsanız sürücüyü "company" contact_type ile kaydetmek
  // veya tax_number doldurmak gerekiyor.
  const payload = {
    data: {
      type: "purchase_bills",
      attributes: {
        item_type: "purchase_bill",
        description: defaultName,
        issue_date: date,
        due_date: date,
        currency: "TRL",
        is_detailed: true,
        invoice_no: invoiceNo || `PUR-${Date.now()}`,
      },
      relationships: {
        supplier: {
          data: { type: "contacts", id: String(supplierContactId) },
        },
        details: {
          data: billLines,
        },
      },
    },
  };

  const result = await parasutFetch("/purchase_bills", {
    method: "POST",
    body: JSON.stringify(payload),
  });

  return { parasutBillId: result.data.id };
};

// Paraşüt PDF indirme — purchase_bill veya sales_invoice için signed URL ister
export const getParasutInvoicePdf = async (parasutId, direction = "PURCHASE") => {
  if (!parasutId) throw new Error("Paraşüt fatura ID yok");
  const endpoint = direction === "PURCHASE" ? "purchase_bills" : "sales_invoices";
  // Paraşüt: /api/v4/{company}/sales_invoices/:id/pdf veya benzeri yok;
  // alternatif: /api/v4/{company}/trackable_jobs ile PDF üret. Pratik için
  // direkt web linkini döner — kullanıcı orada PDF indirebilir.
  const data = await parasutFetch(`/${endpoint}/${parasutId}`);
  const sharings = data?.data?.attributes?.sharings;
  if (sharings?.length > 0 && sharings[0].url) return sharings[0].url;
  // fallback: web linki
  return getParasutWebLink(parasutId, direction);
};

// Sürücü için birden çok teklifi tek alış faturasıyla kes.
// jobs: [{ offerId, advertId, amount, quickly }]
export const issueDriverBulkPurchaseInvoice = async ({ driver, jobs }) => {
  if (!driver?.ID) throw new Error("Sürücü bulunamadı");
  if (!Array.isArray(jobs) || jobs.length === 0) throw new Error("En az bir iş seçmelisin");

  const bank = driver?.Users_BankAccount?.[0];
  const supplierId = await findOrCreateContact({
    name: driver.Users_NameSurname || `Sürücü #${driver.ID}`,
    taxNumber: driver.Users_Tc,
    email: driver.Users_Email || driver.Users_MailAddress,
    phone: driver.Users_PhoneNumber,
    iban: bank?.Users_BankAccount_Iban,
    address: driver.Users_Address,
    accountType: "supplier",
  });

  const total = jobs.reduce((s, j) => s + (Number(j.amount) || 0), 0);
  const lines = jobs.map((j) => ({
    amount: j.amount,
    description: `İlan #${j.advertId} / Teklif #${j.offerId}${j.quickly ? " (Hızlı)" : ""}`,
  }));

  const invoiceNumber = `PUR-${Date.now()}`;
  const { parasutBillId } = await createPurchaseBill({
    supplierContactId: supplierId,
    amount: total,
    description: `LojGo Toplu Hakediş — ${jobs.length} iş`,
    issueDate: new Date().toISOString(),
    vatRate: 0,
    lines,
    invoiceNo: invoiceNumber,
  });

  // Her iş için Invoince kaydı (aynı Parasut_Invoice_ID paylaşılır)
  const createdIds = [];
  for (const j of jobs) {
    const res = await fetch(`https://hiflowapi.stechomeyazilim.info/Invoince`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        Users_ID: driver.ID,
        Direction: "PURCHASE",
        Advert_Offers_ID: j.offerId,
        Invoince_Number: `${invoiceNumber}-${j.offerId}`,
        Created_DateTime: new Date().toISOString(),
        TotalAmount: j.amount,
        Amount_HT: j.amount,
        Amount_VAT: 0,
        Amount_Fees: 0,
        Invoince_Status: "Kesildi",
        Parasut_Invoice_ID: String(parasutBillId),
        Parasut_Status: "issued",
      }),
    });
    if (res.ok) {
      const data = await res.json();
      createdIds.push(data?.ID);
    }
  }

  return { parasutBillId, invoinceIds: createdIds };
};

// Sürücü/çekici için Paraşüt'te alış faturası kes ve Invoince tablosuna kaydet.
export const issueDriverPurchaseInvoice = async ({
  driver,                 // { ID, Users_NameSurname, Users_Tc, Users_Email, Users_PhoneNumber }
  bankAccount,            // { Users_BankAccount_Iban, ... } | null
  amount,
  advertOfferId,
  advertId,
  description,
}) => {
  if (!driver?.ID) throw new Error("Sürücü bulunamadı");
  if (!amount || amount <= 0) throw new Error("Geçerli bir tutar girin");

  // 1) Paraşüt contact (supplier) bul / oluştur
  const supplierId = await findOrCreateContact({
    name: driver.Users_NameSurname || `Sürücü #${driver.ID}`,
    taxNumber: driver.Users_Tc,
    email: driver.Users_Email || driver.Users_MailAddress,
    phone: driver.Users_PhoneNumber,
    iban: bankAccount?.Users_BankAccount_Iban,
    address: driver.Users_Address,
    accountType: "supplier",
  });

  // 2) Alış faturası kes
  const desc = description || `LojGo Hizmet Bedeli — İlan #${advertId} / Teklif #${advertOfferId}`;
  const invoiceNo = `PUR-${Date.now()}-${advertOfferId}`;
  const { parasutBillId } = await createPurchaseBill({
    supplierContactId: supplierId,
    amount,
    description: desc,
    issueDate: new Date().toISOString(),
    vatRate: 0,
    invoiceNo,
  });

  // 3) Local Invoince kaydı (Direction=PURCHASE)
  const invoincePayload = {
    Users_ID: driver.ID,
    Direction: "PURCHASE",
    Advert_Offers_ID: advertOfferId,
    Invoince_Number: invoiceNo,
    Created_DateTime: new Date().toISOString(),
    TotalAmount: amount,
    Amount_HT: amount,
    Amount_VAT: 0,
    Amount_Fees: 0,
    Invoince_Status: "Kesildi",
    Parasut_Invoice_ID: String(parasutBillId),
    Parasut_Status: "issued",
  };

  const res = await fetch(`https://hiflowapi.stechomeyazilim.info/Invoince`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(invoincePayload),
  });
  if (!res.ok) throw new Error("Yerel Invoince kaydı oluşturulamadı");
  const localInv = await res.json();

  return { parasutBillId, invoinceId: localInv?.ID };
};

// ─── SATIŞ FATURASI (Müşteri → LojGo geliri) ─────────────────
// Müşteri (ilan veren) için Paraşüt'te satış faturası kes ve Invoince tablosuna kaydet.
// Direction=SALES. Tutar Advert.Price'tan gelir.
export const issueCustomerSalesInvoice = async ({
  customer,        // Advert.Users — { ID, Users_NameSurname, Users_Tc, Users_Email, ... }
  customerInvoice, // Opsiyonel: Users_Invoince (KdvNumber, VergiDairesi, Adres, Users_Company)
  amount,
  advertOfferId,
  advertId,
  description,
  vatRate = 20,
}) => {
  if (!customer?.ID) throw new Error("Müşteri bilgisi bulunamadı");
  if (!amount || amount <= 0) throw new Error("Geçerli bir tutar girin");

  // 1) Paraşüt contact (customer) bul / oluştur
  const company = customerInvoice?.Users_Company || {};
  const customerName =
    company.Users_Company_Name ||
    customer.Users_NameSurname ||
    customer.NameSurname ||
    `Müşteri #${customer.ID}`;

  const contactId = await findOrCreateContact({
    name: customerName,
    taxNumber: customerInvoice?.KdvNumber || customer.Users_Tc,
    taxOffice: customerInvoice?.VergiDairesi,
    email: customer.Users_MailAddress || customer.Users_Email || customer.Email,
    phone: customer.Users_PhoneNumber,
    address: customerInvoice?.Adres || customer.Users_Address,
    accountType: "customer",
  });

  // 2) Satış faturası kes
  const invoiceNo = `SAL-${Date.now()}-${advertOfferId}`;
  const desc = description || `LojGo Lojistik Hizmet Bedeli — İlan #${advertId}`;

  const fakeInvoice = {
    ID: Date.now(),
    Invoince_Number: invoiceNo,
    Created_DateTime: new Date().toISOString(),
    Amount_HT: amount,
    Invoince_Lines: [{
      ProductName: desc,
      Quantity: 1,
      UnitPrice: amount,
      VatRate: vatRate,
    }],
  };

  const { parasutInvoiceId } = await createSalesInvoice(fakeInvoice, contactId);

  // 3) Local Invoince kaydı (Direction=SALES)
  const vatAmount = amount * (vatRate / 100);
  const total = amount + vatAmount;

  const invoincePayload = {
    Users_ID: customer.ID,
    Direction: "SALES",
    Advert_Offers_ID: advertOfferId,
    Invoince_Number: invoiceNo,
    Created_DateTime: new Date().toISOString(),
    TotalAmount: total,
    Amount_HT: amount,
    Amount_VAT: vatAmount,
    Amount_Fees: 0,
    Invoince_Status: "Kesildi",
    Parasut_Invoice_ID: String(parasutInvoiceId),
    Parasut_Status: "issued",
  };

  const res = await fetch(`https://hiflowapi.stechomeyazilim.info/Invoince`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(invoincePayload),
  });
  if (!res.ok) throw new Error("Yerel Invoince kaydı oluşturulamadı");
  const localInv = await res.json();

  return { parasutInvoiceId, invoinceId: localInv?.ID };
};